import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { WHATSAPP_SUPPORT } from "../../components/layout/PublicFooter";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatDateTime } from "../../lib/format";

export default function MySupport() {
    const [tickets, setTickets] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [activeTicket, setActiveTicket] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ subject: "", message: "", priority: "medium" });
    const [reply, setReply] = useState("");

    const load = async () => {
        const { data } = await api.get("/me/tickets");
        setTickets(data || []);
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (!activeId) { setActiveTicket(null); return; }
        api.get(`/me/tickets/${activeId}`).then(({ data }) => setActiveTicket(data));
    }, [activeId]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/me/tickets", form);
            toast.success("Ticket created");
            setForm({ subject: "", message: "", priority: "medium" });
            setCreating(false);
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    const submitReply = async () => {
        if (!reply.trim()) return;
        try {
            await api.post(`/me/tickets/${activeId}/reply`, { message: reply });
            setReply("");
            const { data } = await api.get(`/me/tickets/${activeId}`);
            setActiveTicket(data);
            toast.success("Reply sent");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    const closeTicket = async () => {
        try {
            await api.post(`/me/tickets/${activeId}/close`);
            const { data } = await api.get(`/me/tickets/${activeId}`);
            setActiveTicket(data);
            await load();
            toast.success("Ticket closed");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    return (
        <DashboardLayout role="client">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="my-support-page">
                <div>
                    <div className="label-mono mb-3">/ Support</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Support tickets</h1>
                </div>
                <button onClick={() => setCreating((v) => !v)} className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="new-ticket-btn">
                    {creating ? "Cancel" : "+ New ticket"}
                </button>
            </div>

            <a
                href={WHATSAPP_SUPPORT.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="support-whatsapp-direct"
                className="panel p-5 mb-6 flex items-center gap-4 hover:border-[#25D366] transition-colors group"
            >
                <div className="w-12 h-12 rounded-full bg-[#25D366]/15 flex items-center justify-center shrink-0 group-hover:bg-[#25D366]/25 transition-colors">
                    <MessageCircle className="w-6 h-6 text-[#25D366]" />
                </div>
                <div className="flex-1">
                    <div className="font-heading font-bold text-base">Direct WhatsApp tech support</div>
                    <div className="text-sm text-avx-muted">Chat with us for installation, activation or urgent issues.</div>
                </div>
                <div className="font-mono text-sm text-[#25D366] font-bold whitespace-nowrap">{WHATSAPP_SUPPORT.display}</div>
            </a>

            {creating && (
                <form onSubmit={submit} className="panel p-6 mb-6 space-y-4" data-testid="ticket-form">
                    <div>
                        <Label className="label-mono">Subject</Label>
                        <Input className="mt-2 bg-avx-surface border-avx-line h-11" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="ticket-subject" />
                    </div>
                    <div>
                        <Label className="label-mono">Priority</Label>
                        <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                            <SelectTrigger className="mt-2 bg-avx-surface border-avx-line h-11" data-testid="ticket-priority">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-mono">Message</Label>
                        <Textarea rows={5} className="mt-2 bg-avx-surface border-avx-line" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="ticket-message" />
                    </div>
                    <button type="submit" className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="ticket-submit">Create ticket</button>
                </form>
            )}

            <div className="grid lg:grid-cols-[1fr_1.5fr] gap-6">
                <div className="space-y-2">
                    {tickets.length === 0 ? (
                        <div className="panel p-8 text-center text-avx-muted text-sm">You don't have any tickets yet.</div>
                    ) : (
                        tickets.map((t) => (
                            <button key={t.id} onClick={() => setActiveId(t.id)} className={`w-full text-left panel p-4 hover:border-avx-blue transition-colors ${activeId === t.id ? "border-avx-blue" : ""}`}
                                data-testid={`ticket-item-${t.id}`}>
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-heading font-bold text-sm">{t.subject}</h3>
                                    <StatusBadge status={t.status} />
                                </div>
                                <div className="font-mono text-[10px] text-avx-muted uppercase tracking-[0.18em] flex justify-between">
                                    <span>#{t.id.slice(0, 8)}</span>
                                    <span>{formatDateTime(t.updated_at)}</span>
                                </div>
                            </button>
                        ))
                    )}
                </div>
                <div className="panel p-6 min-h-[300px]">
                    {!activeTicket ? (
                        <div className="text-avx-muted text-sm">Select a ticket to view the conversation.</div>
                    ) : (
                        <div>
                            <div className="flex justify-between items-start mb-4 pb-4 border-b border-avx-line">
                                <div>
                                    <h2 className="font-heading font-bold text-xl">{activeTicket.subject}</h2>
                                    <div className="font-mono text-[10px] text-avx-muted uppercase tracking-[0.18em] mt-1">
                                        #{activeTicket.id.slice(0, 8)} · Priority: {activeTicket.priority}
                                    </div>
                                </div>
                                <StatusBadge status={activeTicket.status} />
                            </div>
                            <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto">
                                {(activeTicket.messages || []).map((m) => (
                                    <div key={m.id} className={`p-3 rounded-sm border ${m.author_role === "admin" ? "border-avx-blue/40 bg-avx-blue/5" : "border-avx-line bg-avx-surface/40"}`}>
                                        <div className="flex justify-between mb-1">
                                            <span className="text-xs font-mono">{m.author_name} · <span className="text-avx-muted">{m.author_role}</span></span>
                                            <span className="text-[10px] font-mono text-avx-muted">{formatDateTime(m.created_at)}</span>
                                        </div>
                                        <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                                    </div>
                                ))}
                            </div>
                            {activeTicket.status !== "closed" ? (
                                <div className="space-y-3">
                                    <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" rows={3} className="bg-avx-surface border-avx-line" data-testid="reply-textarea" />
                                    <div className="flex gap-2">
                                        <button onClick={submitReply} className="btn-primary px-5 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="reply-submit-btn">
                                            Send reply
                                        </button>
                                        <button onClick={closeTicket} className="btn-secondary px-5 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">
                                            Close ticket
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-xs font-mono text-avx-muted">This ticket is closed.</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
}
